import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import WorkCard from '@/components/base/WorkCard';
import { useSeo } from '@/hooks/useSeo';
import { works } from '@/mocks/works';
import { news } from '@/mocks/news';

type TabKey = 'works' | 'news';

function highlight(text: string, keyword: string) {
  if (!keyword) return text;
  const index = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-accent-100 text-accent-700 rounded px-0.5">
        {text.slice(index, index + keyword.length)}
      </mark>
      {text.slice(index + keyword.length)}
    </>
  );
}

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('keyword') ?? '';
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>('works');

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 - 夏目彩春` : '搜索 - 夏目彩春',
    description: `在夏目彩春影视内容平台中搜索“${keyword}”，快速定位相关影视作品与资讯。`,
    keywords: `夏目彩春搜索,${keyword},影视作品搜索,资讯搜索`,
  });

  const kw = keyword.trim().toLowerCase();

  const matchedWorks = useMemo(() => {
    if (!kw) return [];
    return works.filter((w) =>
      [w.title, w.genre, w.summary, w.director, w.role, w.starring.join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(kw),
    );
  }, [kw]);

  const matchedNews = useMemo(() => {
    if (!kw) return [];
    return news.filter((n) =>
      [n.title, n.summary, n.category, n.content.map((c) => c.text).join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(kw),
    );
  }, [kw]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    setParams({ keyword: value });
    setTab('works');
  };

  const activeList = tab === 'works' ? matchedWorks : matchedNews;
  const total = matchedWorks.length + matchedNews.length;
  const suggestions = works.slice(0, 4);

  return (
    <div className="bg-background-50">
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-3xl">
          <span className="font-label text-xs tracking-[0.25em] uppercase text-accent-600">Search</span>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950 mt-2 mb-6">站内搜索</h1>

          <form onSubmit={submit} className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-lg p-2">
            <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入作品名称、题材或资讯关键词"
              className="flex-1 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          {keyword && (
            <p className="mt-5 text-sm text-foreground-600">
              关键词 <span className="font-medium text-primary-600">“{keyword}”</span>：
              共找到 <span className="font-label text-accent-600">{total}</span> 条结果
              （影视作品 {matchedWorks.length} · 资讯 {matchedNews.length}）
            </p>
          )}
        </div>

        {keyword && total > 0 && (
          <>
            <div className="mt-7 flex items-center gap-2 border-b border-background-200/80">
              <button
                type="button"
                onClick={() => setTab('works')}
                className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors cursor-pointer ${
                  tab === 'works'
                    ? 'border-accent-500 text-primary-600 font-medium'
                    : 'border-transparent text-foreground-600 hover:text-primary-600'
                }`}
              >
                影视作品（{matchedWorks.length}）
              </button>
              <button
                type="button"
                onClick={() => setTab('news')}
                className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors cursor-pointer ${
                  tab === 'news'
                    ? 'border-accent-500 text-primary-600 font-medium'
                    : 'border-transparent text-foreground-600 hover:text-primary-600'
                }`}
              >
                资讯（{matchedNews.length}）
              </button>
            </div>

            <div className="mt-7">
              {tab === 'works' ? (
                matchedWorks.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {matchedWorks.map((work) => (
                      <WorkCard key={work.id} work={work} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-500 py-10 text-center">没有匹配的影视作品，试试切换到「资讯」标签。</p>
                )
              ) : matchedNews.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {matchedNews.map((n) => (
                    <Link
                      key={n.id}
                      to={`/news/${n.id}`}
                      className="group flex gap-4 bg-background-100 border border-background-200/80 rounded-lg p-4 hover:border-primary-300 transition-colors"
                    >
                      <div className="w-28 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={n.cover}
                          alt={`${n.title} 配图`}
                          title={`${n.title} - 夏目彩春影视资讯`}
                          className="w-full h-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700">{n.category}</span>
                          <span className="text-xs text-foreground-400 font-label">{n.date}</span>
                        </div>
                        <h3 className="mt-1.5 text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {highlight(n.title, keyword)}
                        </h3>
                        <p className="mt-1 text-sm text-foreground-600 line-clamp-2">{highlight(n.summary, keyword)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500 py-10 text-center">没有匹配的资讯，试试切换到「影视作品」标签。</p>
              )}
            </div>
          </>
        )}

        {keyword && total === 0 && (
          <div className="mt-10">
            <div className="flex flex-col items-center text-center py-12 bg-background-100 border border-background-200/80 rounded-lg">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-5">
                <i className="ri-search-eye-line text-3xl" />
              </div>
              <h3 className="font-heading font-bold text-lg text-foreground-900">没有找到与“{keyword}”相关的内容</h3>
              <p className="mt-2 text-sm text-foreground-500 max-w-md">
                换个关键词试试，或从下方精选作品中开始浏览。
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/category"
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览全部作品
                </Link>
                <Link
                  to="/news"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  查看全部资讯
                </Link>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2.5">
                <span className="w-1.5 h-5 rounded-full bg-accent-500" />
                为你推荐
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {suggestions.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </div>
          </div>
        )}

        {!keyword && (
          <div className="mt-12">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5 flex items-center gap-2.5">
              <span className="w-1.5 h-5 rounded-full bg-accent-500" />
              热门作品推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {suggestions.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}